import {Injectable} from '@angular/core';
import {DoubleSide, FrontSide, Geometry, Line, LineBasicMaterial, Material, Mesh, MeshBasicMaterial, SphereGeometry, Vector3} from 'three';
import {ModelDataService} from '../../Utils/model-data.service';
import {SceneService} from '../Scene/scene.service';
import {ContainerService} from '../../Utils/container.service';
import {MouseControlsService} from '../Control/mouse-controls.service';
import {RenderService} from '../Scene/render.service';
import {EditorControlService} from '../../API/Public/editor-control.service';
import {CanvasEvent} from '../Control/controls';

@Injectable({
    providedIn: 'root'
})
export class DistanceMeasureService {

    public distance = 0;
    private measurePoint1 = new Vector3();
    private measurePoint2 = new Vector3();
    private firstMeasurePoint = true;
    private _groupName = 'MeasurePoints';

    constructor(private modelDataService: ModelDataService,
                private sceneService: SceneService,
                private containerService: ContainerService,
                private mouseControlService: MouseControlsService,
                private renderService: RenderService,
                private editorControlService: EditorControlService) {

        this.editorControlService.registerControl('measure', {
            events: new Map([
               [CanvasEvent.Down, (event) => this.setMeasurePointForEvent(event)]
            ]),
            init: this.startMeasuring.bind(this),
            finish: this.endMeasuring.bind(this),
            pan: false,
            controlMode: false
        });

    }

    /**
     * starts the measuring mode
     * sets 3d human model's opacity
     */
    public startMeasuring() {
        const model = <Mesh>this.containerService.renderGroup.children[0];
        (<Material>model.material).opacity = 0.75;
        (<Material>model.material).transparent = true;
        (<Material>model.material).side = FrontSide;

        this.renderService.render();
    }

    /**
     * ends measuring mode
     * model opacity is set to 1 and all markers are removed
     */
    public endMeasuring() {
        const model = <Mesh>this.containerService.renderGroup.children[0];
        if (model) {
            (<Material>model.material).transparent = false;
            (<Material>model.material).side = DoubleSide;
            this.removeMarkers();
            this.renderService.render();
        }
    }

    /**
     * calculate distance between two markers and converts it to world distance
     */
    public measure() {
        const dist = this.measurePoint1.distanceTo(this.measurePoint2);
        this.distance = (dist * (this.modelDataService.height / this.modelDataService.heightOfModelCm)) * 10;
        // * (10 / this.modelDataService.scaleFactor);
    }

    /**
     * remove markers from 3D render space
     */
    public removeMarkers() {
        this.sceneService.removeGroup(this._groupName);
    }

    // -------------------------------------------------------------------------------------------------------------------------------------

    /**
     * if first measuring point is set, a new render Object is added and the first marker is set
     * else measuring is complete --> distance is calculated
     * @param point
     */
    private setMeasurePoint(point: Vector3) {

        if (this.firstMeasurePoint) {
            this.firstMeasurePoint = false;
            this.distance = 0;
            this.removeMarkers();
            this.sceneService.addGroup(this._groupName);
            this.measurePoint1 = point;
        } else {
            this.firstMeasurePoint = true;
            this.measurePoint2 = point;
            this.measure();
        }
        this.addMarker(point);

    }

    /**
     * place measure point on 3D model
     * @param event
     */
    private setMeasurePointForEvent(event: MouseEvent) {
        // if (event.buttons !== 2 && this._multitouch === 1 && this.containerService.loadingStatus === 1) {
        if (event.buttons !== 2 && this.containerService.loadingStatus === 1) {
            const mouse = this.mouseControlService.getCanvasMouse(event);
            const model = <Mesh>this.containerService.renderGroup.children[0];
            const intersects = this.mouseControlService.getMouseModelIntersection(mouse, model);

            if (intersects.length > 0) {
                this.setMeasurePoint(intersects[0].point);
            }

        }
        this.renderService.render();
    }

    /**
     * add marker to render object
     * if two markers are set --> draw line between these two points
     * @param point: Vector3
     */
    private addMarker(point: Vector3) {
        const color = 0xff000f;

        const sphereGeo = new SphereGeometry(this.containerService.pointSize);
        const sphere = new Mesh(sphereGeo, new MeshBasicMaterial({color: color}));

        sphere.position.x = point.x;
        sphere.position.y = point.y;
        sphere.position.z = point.z;
        this.sceneService.getGroupByName(this._groupName).add(sphere);

        if (this.firstMeasurePoint) {
            const lineGeo = new Geometry();
            lineGeo.vertices.push(this.measurePoint1, this.measurePoint2);
            const line = new Line(lineGeo, new LineBasicMaterial({color: color}));
            this.sceneService.getGroupByName(this._groupName).add(line);
        }

    }

}
